import { FC, Suspense, useContext, useEffect, useState } from "react";
import { CancelTokenSource } from "axios";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { wrapPromise } from "go-core";
import { LoadingContainer } from "go-core/components/Loading";
import { ExternalListSelectedFilter } from "go-list/core/components/Filter/services/types";
import ReportData from "go-report/reports";
import {
	getChartReport,
	getChartType,
	getLastMonthFilterBetween,
	getReport,
	getReportCustomFieldsResources,
} from "go-report/reports/services/report-service";
import { getSelectedSegmentForReportConfig } from "go-report/reports/services/segment-service";
import { ReportConfig } from "go-report/reports/services/types";
import { selectOrganization } from "go-security/services/organizations/selectors";
import { SegmentType } from "go-segment/components/types";
import { SegmentContext } from "go-segment/context";
import { apiReport } from "../../../../../../../../services/Api/Report/apiReport";
import {
	getCommonBarcodeFilter,
	getCommonBarcodeGroup,
	getCommonPaymentStatusFilter,
	getCommonPaymentStatusGroup,
	getCommonPaymentStatusGroupInfo,
} from "../../../../../../../../utils/reports";
import ReportCategoriesProductUtils from "../../../../../../../../utils/reports/ReportCategoriesProductUtils";
import ReportProductUtils from "../../../../../../../../utils/reports/ReportProductUtils";
import { ReportSaleUtils } from "../../../../../../../../utils/reports/ReportSaleUtils";
import ReportShareUtils from "../../../../../../../../utils/reports/ReportShareUtils";

interface ListState {
	resource?: Record<string, any>;
}

const listName = "PRODUCT_REPORT";
const resourceType = "PRODUCT_REPORT";

const Report: FC<ListState> = ({ resource }): JSX.Element | null => {
	const { t } = useTranslation();
	const organization = useSelector(selectOrganization);
	const [filtersParams, setFilterParams] = useState("");
	const segmentContext = useContext(SegmentContext);

	if (!resource) return null;
	const data = resource.read();

	let config = {
		aggregatePrefix: "sales",
		reportConfigId: "products",
		type: "order_item",
		chart: {
			title: t("modules.item_group_report.field.report.title"),
			lineTitle: "",
			aggregate: "total_money",
			type: getChartType(),
			parseGroup: (group: string) => {
				if (group === "HOUR_OF_DAY") return "CLOSED_AT_HOUR_OF_DAY";
				if (group === "DAY_OF_MONTH") return "REPORT_DATE_DAY_OF_MONTH";
				if (group === "DATE") return "REPORT_DATE_DATE";
				if (group === "DAY_OF_WEEK") return "REPORT_DATE_DAY_OF_WEEK";
				if (group === "MONTH") return "REPORT_DATE_MONTH";
				return group;
			},
		},
		selectedColumns: ReportCategoriesProductUtils.getSelectedColumns("products"),
		groups: [
			...ReportCategoriesProductUtils.getGroups(t),
			...ReportShareUtils.getGroups(t),
			...ReportProductUtils.getGroups(t),
			...ReportSaleUtils.getGroups(t),
			getCommonBarcodeGroup(t),
			getCommonPaymentStatusGroup(t),
		],
		selectedGroups: ["PRODUCT"],
		showPercent: true,
		columns: ReportCategoriesProductUtils.getColumns(t),
		externalSelectedFilters: [
			{
				id: "date_range",
				filterType: "date",
				value: getLastMonthFilterBetween().split("=")[1],
				condition: getLastMonthFilterBetween().split("=")[0],
				visible: true,
			} as ExternalListSelectedFilter,
		],
		groupsInfo: {
			...ReportShareUtils.getGroupsInfo(t),
			...ReportSaleUtils.getGroupsInfo(t),
			PRODUCT: (groupByValue: Record<string, any>) => {
				if (groupByValue.id && groupByValue.name && groupByValue.id !== "-1")
					return (
						<Link to={`/${organization.id}/menu/item_groups/${groupByValue.id}`}>{groupByValue.name}</Link>
					);
				return groupByValue.name === "empty" ? "-" : groupByValue.name;
			},
			...getCommonPaymentStatusGroupInfo(t),
		},
		filters: [
			...ReportCategoriesProductUtils.getFilters(t, filtersParams),
			...ReportCategoriesProductUtils.getProductsFilters(t, filtersParams),
			...ReportSaleUtils.getFilters(t),
			getCommonBarcodeFilter(t, filtersParams),
			getCommonPaymentStatusFilter(t),
		],
		segments: [
			{
				id: "all",
				name: t("common.word.all", { ns: "lib" }),
				slug: "all",
				columns: ReportCategoriesProductUtils.getSelectedColumns("products"),
				filters: [
					{
						filterId: "date_range",
						filterType: "date",
						value: getLastMonthFilterBetween().split("=")[1],
						condition: getLastMonthFilterBetween().split("=")[0],
					},
				],
			},
		],
		fetch: (params: Record<string, any> = {}, sourceToken: CancelTokenSource) => {
			setFilterParams(params.f);
			return Promise.all([
				getReport(
					apiReport.getReportsCustom,
					params,
					organization.id,
					config.filters,
					config.type,
					sourceToken,
					undefined,
					"ORDERS"
				),
				getChartReport(
					apiReport.getReportsCustom,
					params,
					"PRODUCT",
					organization.id,
					config.filters,
					config.type,
					sourceToken,
					5,
					"ORDERS"
				),
			]);
		},
		fetchChartData: (params: Record<string, any> = {}, sourceToken: CancelTokenSource) => {
			return getChartReport(
				apiReport.getReportsCustom,
				params,
				"PRODUCT",
				organization.id,
				config.filters,
				config.type,
				sourceToken,
				5,
				"ORDERS"
			);
		},
		saveSegment: (segment: SegmentType) => {
			return segmentContext.save(listName, resourceType, segment);
		},
		exportConfig: {
			title: t("modules.item_group_report.field.export_config.title"),
			filename: t("modules.item_group_report.field.export_config_filename.title"),
			organization: organization.name,
			exportCSVWithEveryColumn: (params: Record<string, any>) =>
				apiReport.getProductReportsCSVWithEveryColumn(params, true),
			taxIdNo: organization?.more?.print_company_on_pdf ? organization.more?.company_tax_id_no : undefined,
			company: organization?.more?.print_company_on_pdf ? organization.more?.company_name : undefined,
			pdfOrientation: organization?.more?.pdf_orientation,
			pdfFontSize: organization?.more?.default_pdf_font_size?.toString(),
		},
		selectedSegment: getSelectedSegmentForReportConfig(data.segments, "all"),
		withCompareDateRange: true,
	} as ReportConfig;

	data.fields = getReportCustomFieldsResources(data.fields, ["ITEM", "ORDER"], t);

	config = {
		...config,
		externalSegments: data.segments,
		fields: config.fields ? [...config.fields, ...data.fields] : data.fields,
		filterValues: data.filter_values,
	};

	return <ReportData config={config} />;
};

const OrganizationReportsProductsPage = (): JSX.Element => {
	const [resource, setResource] = useState<Record<string, any>>();
	const { t } = useTranslation();
	const segmentContext = useContext(SegmentContext);
	const customFieldResourceTypes = [
		{
			type: "ITEM",
			name: t(`enums.custom_fields.resources.ITEM`),
		},
		{
			type: "ORDER",
			name: t(`enums.custom_fields.resources.ORDER`),
		},
	];

	useEffect(() => {
		setResource(wrapPromise(segmentContext.get(listName, resourceType, customFieldResourceTypes)));
	}, []);

	return (
		<Suspense fallback={<LoadingContainer />}>
			<Report resource={resource} />
		</Suspense>
	);
};

export default OrganizationReportsProductsPage;
